import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { HR_EVIDENCE_UPLOAD_LIMITS, credentialDetailsSchema, type ChecklistItem, type DirectRecordInput } from '@vigeo/shared';
import { useSectionActive, tokens } from '@vigeo/ui';
import { ComplianceEvidenceFields, type ComplianceEvidenceDraft } from './ComplianceEvidenceFields.js';
import { useRecording } from './RecordingRowProvider.js';
import { recordRequirement } from '@/lib/api.js';
import { HrFileUpload, type HrUpload } from '@/features/uploads';

/**
 * Where a piece of evidence is held (#188, ADR-009 Q4). An uploaded file for anything Vigeo holds;
 * an EXTERNAL link only for an outside system of record — QuickBooks Workforce, a registry lookup.
 */
type EvidenceChoice = 'upload' | 'link';

interface EvidenceDraft {
  readonly choice: EvidenceChoice;
  readonly url: string;
  readonly upload: HrUpload | null;
}

const NO_EVIDENCE: EvidenceDraft = { choice: 'upload', url: '', upload: null };

/** Present and usable: a clean upload, or an https external link. An upload still scanning is neither. */
function evidenceReady(draft: EvidenceDraft): boolean {
  return draft.choice === 'upload' ? draft.upload?.usable === true : draft.url.startsWith('https://');
}

/** An upload that exists but cannot be saved yet: it holds the save until its scan is clean. */
function evidenceWaiting(draft: EvidenceDraft): boolean {
  return draft.choice === 'upload' && draft.upload !== null && !draft.upload.usable;
}

function EvidenceInput({ label, hint, required, value, onChange, personId, recordId }: {
  readonly label: string;
  readonly hint: string;
  readonly required: boolean;
  readonly value: EvidenceDraft;
  readonly onChange: (next: EvidenceDraft) => void;
  readonly personId: string;
  readonly recordId: string;
}) {
  return <>
    <RadioGroup layout="horizontal" aria-label={`${label}: how it is held`} value={value.choice} onChange={(_, d) => onChange({ ...value, choice: d.value as EvidenceChoice })}>
      <Radio value="upload" label="Upload the file" />
      <Radio value="link" label="External link (outside system of record)" />
    </RadioGroup>
    {value.choice === 'upload'
      ? <HrFileUpload label={label} hint={hint} required={required} target={{ kind: 'evidence', personId, recordId }} acceptedTypes={HR_EVIDENCE_UPLOAD_LIMITS.acceptedTypes} onChange={(upload) => onChange({ ...value, upload })} />
      : <Field label={`${label} — external link`} required={required} hint="Only for a record held in another system, such as QuickBooks Workforce or a registry lookup."><Input type="url" value={value.url} onChange={(_, d) => onChange({ ...value, url: d.value })} /></Field>}
  </>;
}

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  trigger: { minHeight: '2rem' },
  /**
   * The form in the page, under its row (PG15). Marked off by a left edge and a tint rather than
   * a border all round: it belongs to the row above it, and a box would read as a separate card.
   */
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    marginBlockStart: tokens.spacingVerticalS,
    marginBlockEnd: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalL,
    borderInlineStartWidth: tokens.strokeWidthThick,
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: tokens.colorBrandStroke1,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  /** Cancel on the left and the one primary on the right, the same order every pop-up uses. */
  panelActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

/** `<input type="date">` wants YYYY-MM-DD, and today is the overwhelmingly common answer. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface RecordRequirementProps {
  readonly personId: string;
  readonly item: ChecklistItem;
}

/**
 * HR closing a row themselves (#85).
 *
 * Orientation is the case that makes this necessary: HR runs it in a room, the candidate has
 * nothing to upload, and without this the person finishes everything the portal asks and
 * stays "Not ready" forever.
 *
 * The date asked for is when it HAPPENED, not when HR got round to typing it in. That
 * difference is the anchor the engine uses for anything on a renewal interval, so defaulting
 * it silently to now would quietly move every future due date.
 */
/**
 * The button on the row. It asks the checklist to open this row's form rather than opening one
 * itself, because PG15 allows only one open at a time and switching away from typing must ask
 * first — neither of which a row can decide alone.
 */
export function RecordRequirementTrigger({ item }: { readonly item: ChecklistItem }) {
  const styles = useStyles();
  const recording = useRecording();
  const open = recording?.openId === item.requirementKey;
  return (
    <Button
      size="small"
      className={styles.trigger}
      aria-expanded={open}
      onClick={() => recording?.requestOpen(item.requirementKey)}
    >
      Record
    </Button>
  );
}

/**
 * Mounts the form only for the row being recorded.
 *
 * MOUNTING RATHER THAN HIDING is what gives each row a clean form: unmounting throws the previous
 * row's fourteen fields away, which is exactly what Discard means. It also keeps the hook out of
 * the caller's `map`, which cannot call `useRecording` per item.
 */
export function RecordRequirementWhenOpen({ personId, item }: RecordRequirementProps) {
  const recording = useRecording();
  if (recording?.openId !== item.requirementKey) return null;
  return <RecordRequirementPanel personId={personId} item={item} />;
}

export function RecordRequirementPanel({ personId, item }: RecordRequirementProps) {
  const sectionActive = useSectionActive();
  const styles = useStyles();
  const client = useQueryClient();
  const recording = useRecording();
  const [occurredOn, setOccurredOn] = useState(today());
  const [reference, setReference] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [source, setSource] = useState('');
  const [outcome, setOutcome] = useState<'clear' | 'not-clear' | 'inconclusive'>('clear');
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState<EvidenceDraft>(NO_EVIDENCE);
  // Every file this record rests on is reserved under one record id, which becomes the saved
  // record's own id — so the approval commits them through the same store-file path (#188).
  const [recordId, setRecordId] = useState(() => crypto.randomUUID());
  const [credential, setCredential] = useState(() => credentialDetailsSchema.parse({}));
  const [evaluatorTitle, setEvaluatorTitle] = useState('');
  const [complianceEvidence, setComplianceEvidence] = useState<ComplianceEvidenceDraft>({});
  const requiresReview = item.compliance !== undefined && item.compliance.evidenceMode !== 'standard';
  const [formEvidence, setFormEvidence] = useState<Record<string, EvidenceDraft>>({});

  /**
   * WHETHER ANYTHING HAS BEEN TYPED, as a snapshot comparison rather than a flag each handler sets.
   *
   * The form has fourteen or fifteen fields and several are objects. A `touched` boolean set in
   * every `onChange` is one forgotten call away from telling the checklist that a half-filled
   * credential is clean, and the person loses what they typed with no prompt. Comparing the whole
   * draft to the one this form opened with cannot drift when somebody adds a field.
   *
   * `occurredOn` defaults to today, so it is part of the opening snapshot and does not count as
   * typing by itself.
   */
  const draft = JSON.stringify({ occurredOn, reference, expiresOn, performedBy, source, outcome, note, evidence, credential, evaluatorTitle, complianceEvidence, formEvidence });
  const latest = useRef(draft);
  latest.current = draft;
  const opened = useRef<string | null>(null);
  if (opened.current === null) opened.current = draft;
  useEffect(() => {
    recording?.registerDirty(() => latest.current !== opened.current);
  }, [recording]);

  const forms = item.competencyForms;
  const isCheck = item.kind === 'external-check';
  const incompleteAssessment = forms != null && (forms.length === 0 || performedBy.trim() === '' || evaluatorTitle.trim() === '' || forms.some((form) => !evidenceReady(formEvidence[form.jobProfileKey] ?? NO_EVIDENCE)));
  const waiting = evidenceWaiting(evidence) || Object.values(formEvidence).some(evidenceWaiting);
  const linkOrUpload = (draft: EvidenceDraft) => draft.choice === 'link'
    ? (draft.url === '' ? {} : { evidenceUrl: draft.url })
    : (draft.upload === null ? {} : { evidenceUploadId: draft.upload.uploadId });

  const record = useMutation({
    mutationFn: () => {
      const payload: DirectRecordInput = {
        ...complianceEvidence,
        // Parsed as UTC midnight. `new Date('2027-03-12')` and `new Date('2027-03-12T00:00')`
        // differ by a timezone offset, which is how a date lands on the previous day.
        occurredAt: `${occurredOn}T00:00:00.000Z`,
        reference,
        expiresAt: expiresOn === '' ? null : `${expiresOn}T00:00:00.000Z`,
        performedBy,
        source,
        outcome,
        note,
        evidenceUrl: evidence.choice === 'link' && evidence.url !== '' ? evidence.url : null,
        ...(evidence.choice === 'upload' && evidence.upload !== null ? { evidenceUploadId: evidence.upload.uploadId } : {}),
        // Only when a file was uploaded: the id is the reservation those files were staged under.
        ...([evidence, ...Object.values(formEvidence)].some((draft) => draft.choice === 'upload' && draft.upload !== null) ? { recordId } : {}),
        ...(item.kind === 'credential' ? { credentialDetails: credential } : {}),
        ...(forms == null ? {} : { competency: forms.map((form) => ({ ...form, evaluator: performedBy, evaluatorTitle, ...linkOrUpload(formEvidence[form.jobProfileKey] ?? NO_EVIDENCE) })) }),
      };
      return recordRequirement(personId, item.requirementKey, payload);
    },
    onSuccess: async () => {
      // Recorded, so there is nothing to discard: closed directly rather than through
      // `requestClose`, which would ask about the very typing that has just been saved.
      opened.current = latest.current;
      recording?.requestClose();
      // The saved record now owns that id; a later record starts a new reservation.
      setRecordId(crypto.randomUUID());
      setEvidence(NO_EVIDENCE);
      setFormEvidence({});
      // The checklist is derived from this evidence, so it must be refetched with it.
      await client.invalidateQueries({ queryKey: ['checklist', personId] });
    },
  });

  // The section collapsing takes the open form with it, as the dialog used to close.
  if (!sectionActive) return null;

  return (
    <section className={styles.panel} aria-label={`Record: ${item.label}`}>
      <div className={styles.body}>
            <Text weight="semibold">Record: {item.label}</Text>
              <Text>
                Use this when our office did it or received it directly — the candidate has
                nothing to send for this one.
              </Text>

              {record.isError ? (
                <MessageBar intent="error">
                  <MessageBarBody>{(record.error as Error).message}</MessageBarBody>
                </MessageBar>
              ) : null}

              <Field label="When did it happen?" required hint="Not today's date unless it happened today.">
                <Input type="date" value={occurredOn} onChange={(_, d) => setOccurredOn(d.value)} />
              </Field>

              <Field label="Reference" hint="A certificate number, a vendor reference, or who ran it. Optional.">
                <Input value={reference} maxLength={200} onChange={(_, d) => setReference(d.value)} />
              </Field>
              <Field label={forms == null ? 'Performed by' : 'Evaluator'} required={isCheck || forms != null || requiresReview} hint="Who actually performed the check or assessment. Your sign-in is recorded separately.">
                <Input value={performedBy} maxLength={200} onChange={(_, d) => setPerformedBy(d.value)} />
              </Field>
              {isCheck || requiresReview ? <>
                <Field label="Source or registry" required><Input value={source} maxLength={200} onChange={(_, d) => setSource(d.value)} /></Field>
                <Field label="Result" required><Select value={outcome} onChange={(_, d) => setOutcome(d.value as typeof outcome)}>
                  <option value="clear">Clear</option><option value="not-clear">Not clear — follow-up required</option><option value="inconclusive">Inconclusive — review required</option>
                </Select></Field>
              </> : null}
              <EvidenceInput label="Evidence" hint="The retained supporting record. It is checked for malware before it can be saved." required={requiresReview} value={evidence} onChange={setEvidence} personId={personId} recordId={recordId} />
              {requiresReview && item.compliance ? <ComplianceEvidenceFields rule={item.compliance} value={complianceEvidence} onChange={setComplianceEvidence} /> : null}
              {item.compliance?.reviewQuestion ? <Text role="status">{item.compliance.reviewQuestion} Record the approved decision in Agency settings before completing this requirement.</Text> : null}
              <Field label="Notes"><Textarea value={note} maxLength={1000} onChange={(_, d) => setNote(d.value)} /></Field>
              {item.kind === 'credential' ? <>
                <Field label="Credential type"><Input value={credential.credentialType} onChange={(_, d) => setCredential({ ...credential, credentialType: d.value })} /></Field>
                <Field label="Issuing state or jurisdiction"><Input value={credential.issuingState} onChange={(_, d) => setCredential({ ...credential, issuingState: d.value })} /></Field>
                <Field label="Verification status"><Select value={credential.verificationStatus} onChange={(_, d) => setCredential({ ...credential, verificationStatus: d.value as typeof credential.verificationStatus })}>
                  <option value="not-verified">Not verified</option><option value="verified">Verified</option><option value="not-clear">Not clear</option><option value="inconclusive">Inconclusive</option>
                </Select></Field>
                <Field label="Verification source"><Input value={credential.verificationSource} onChange={(_, d) => setCredential({ ...credential, verificationSource: d.value })} /></Field>
                <Field label="Verified by"><Input value={credential.verifiedBy} onChange={(_, d) => setCredential({ ...credential, verifiedBy: d.value })} /></Field>
                <Field label="Verification date"><Input type="date" value={credential.verifiedAt?.slice(0, 10) ?? ''} onChange={(_, d) => setCredential({ ...credential, verifiedAt: d.value === '' ? null : `${d.value}T00:00:00.000Z` })} /></Field>
                <Field label="Verification notes"><Textarea value={credential.verificationNote} maxLength={1000} onChange={(_, d) => setCredential({ ...credential, verificationNote: d.value })} /></Field>
                <Field label="Original issue date text" hint="Preserve unclear dates as written; do not guess."><Input value={credential.originalIssuedDate} onChange={(_, d) => setCredential({ ...credential, originalIssuedDate: d.value })} /></Field>
                <Field label="Original expiry date text"><Input value={credential.originalExpiryDate} onChange={(_, d) => setCredential({ ...credential, originalExpiryDate: d.value })} /></Field>
              </> : null}
              {forms == null ? null : <>
                <Field label="Evaluator title or qualification" required><Input value={evaluatorTitle} onChange={(_, d) => setEvaluatorTitle(d.value)} /></Field>
                {forms.length === 0 ? <Text>No controlled competency form is mapped for this person’s roles. Resolve the role with HR first.</Text> : forms.map((form) => <EvidenceInput key={form.jobProfileKey}
                  label={`${form.jobProfileKey}: ${form.assessmentType} assessment — ${form.formId}`} hint="The completed role-specific form." required
                  value={formEvidence[form.jobProfileKey] ?? NO_EVIDENCE} onChange={(next) => setFormEvidence((current) => ({ ...current, [form.jobProfileKey]: next }))}
                  personId={personId} recordId={recordId} />)}
              </>}

              {/* `dated` is the timing that carries an interval; `unsettled` is one whose
                  interval HR has not decided yet, and asking for an expiry there is how a
                  guess gets recorded as policy.

                  `expiryFromDocument` splits that further, and `timing` alone cannot: an
                  interval row is dated too and takes its date from the RULE, but a
                  credential-date row is tracked by the date on the card. Recording one of
                  those without a date mints a credential the engine can only read as
                  indeterminate, which leaves the person on "Needs review" for ever (#120).
                  So the server refuses it, and this must not invite what the server
                  refuses. */}
              {item.timing === 'dated' ? (
                <Field
                  label="Expires"
                  required={item.expiryFromDocument}
                  hint={
                    item.expiryFromDocument
                      ? 'As printed on the document. This one is tracked by that date.'
                      : 'Leave blank if it does not expire.'
                  }
                >
                  <Input type="date" value={expiresOn} onChange={(_, d) => setExpiresOn(d.value)} />
                </Field>
              ) : null}
      </div>
      <div className={styles.panelActions}>
              <Button onClick={() => recording?.requestClose()} disabled={record.isPending}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                /* The same rule the server applies, so the button is never the thing that
                   invites a refusal. A credential-date row without its date is refused. */
                disabled={
                  occurredOn === '' ||
                  (outcome === 'clear' && Boolean(item.compliance?.reviewQuestion)) ||
                  (requiresReview && outcome === 'clear' && (performedBy.trim() === '' || !evidenceReady(evidence) || !complianceEvidence.policyVersion?.trim())) ||
                  // An upload still being scanned holds the save; the server refuses it too.
                  waiting ||
                  (item.expiryFromDocument && expiresOn === '') ||
                  (isCheck && (performedBy.trim() === '' || source.trim() === '')) ||
                  incompleteAssessment ||
                  record.isPending
                }
                onClick={() => record.mutate()}
              >
                {record.isPending ? 'Recording…' : 'Record it'}
              </Button>
      </div>
    </section>
  );
}
